import React, { useCallback, useEffect, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Divider } from '@mui/material';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { dateString, wrapList } from 'shared/utils';
import SimpleDataTable from 'shared/components/table/SimpleDataTable';
import { getTenantMacs } from 'shared/api/tenants';
import { getGroupDpsk, getSmartzone } from 'shared/api/wireless';
import { useRequest } from 'shared/hooks';
import { getBuildingDot1x } from 'shared/api/buildings';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import { Section } from 'shared/components/styled/layouts';
import { DataReducerActions } from 'shared/reducers';
import TenantWifiInfo from './TenantWifiInfo';
import DeviceDeleteButton from './DeviceDeleteButton';
import DeviceForm from './DeviceForm';
import DeviceModal from './DeviceModal';

const initialState = {
  deviceId: 0,
  groupDpsk: {},
  groupDpskIsMissing: false,
  nasProfile: {},
  permissions: {},
  selectedDevices: {},
  smartzone: {},
  updateCount: 0,
};

function tenantDeviceReduce(state, { action, payload }) {
  switch (action) {
    case 'forceUpdate':
      return { ...state, updateCount: state.updateCount + 1 };
    case 'reset':
      return {
        ...state,
        selectedDevices: {},
        updateCount: state.updateCount + 1,
      };
    case 'select':
      return {
        ...state,
        selectedDevices: payload,
      };
    case 'set':
      return {
        ...state,
        ...payload,
      };
    default:
      return state;
  }
}

/**
 * @param {Object} props
 * @param {Object} props.tenantState - Tenant state object
 */
export default function TenantDeviceView({ tenantState }) {
  const history = useHistory();
  const { search } = useLocation();
  const { request, isLoading } = useRequest();

  const [state, dispatch] = useReducer(tenantDeviceReduce, {
    ...tenantState,
    ...initialState,
  });

  const {
    building,
    deviceId,
    groupDpsk,
    groupDpskIsMissing,
    selectedDevices,
    smartzone,
    updateCount,
    data: {
      id: tenantId,
    },
  } = state;

  // Safely read data.
  // When tenant is archived building/unit won't be defined.
  const { id: buildingId, unit, dot1x } = building || {};
  const unitId = unit && unit.id;
  const smartzoneId = dot1x && dot1x.smartzone_id;

  useEffect(() => {
    async function loadSmartzone() {
      const result = await request(getSmartzone(smartzoneId));
      if (result) {
        dispatch({ action: DataReducerActions.set, payload: { smartzone: result } });
      }
    }

    if (smartzoneId > 0) {
      loadSmartzone();
    }
  }, [request, smartzoneId]);

  useEffect(() => {
    async function checkDpsk() {
      const result = await request(getGroupDpsk(unitId));

      if (result === undefined) return;
      dispatch({
        action: DataReducerActions.set,
        payload: {
          groupDpsk: result.data || {},
          groupDpskIsMissing: result.data === null,
        },
      });
    }

    if (smartzone.id > 0) {
      checkDpsk();
    }
  }, [request, smartzone, unitId, updateCount]);

  // Effect for updating active mac based on url.
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const newDeviceId = parseInt(searchParams.get('id'), 10);
    dispatch({ action: DataReducerActions.set, payload: { deviceId: newDeviceId || 0 } });
  }, [search]);

  useEffect(() => {
    (async () => {
      const dot1xResponse = await request(getBuildingDot1x(buildingId));
      if (dot1xResponse) {
        dispatch({
          action: DataReducerActions.set,
          payload: { nasProfile: dot1xResponse.nas_profile },
        });
      }
    })();
  }, [buildingId, request]);

  const forceUpdate = () => dispatch({ action: 'forceUpdate' });

  const setActiveDevice = (newId) => {
    if (newId < 1) {
      history.push('?');
      return;
    }

    const searchParams = new URLSearchParams(search);
    searchParams.set('id', newId);
    history.push(`?${searchParams.toString()}`);
  };

  const afterDelete = () => {
    dispatch({ action: 'reset' });
  };

  const columns = [
    {
      name: 'Mac',
      jsonKey: 'mac',
      formatter: (device, _, i, rows) => (
        <DeviceModal
          active={device.id === deviceId}
          changeActiveDevice={(n) => setActiveDevice(wrapList(rows, i, n).id)}
          device={device}
          forceUpdate={forceUpdate}
          hasMore={rows.length > 1}
          editEnabled={tenantState.macEditCheck}
          setActiveDevice={setActiveDevice}
          tenant={tenantState.data}
          radiusDataAccessPermission={tenantState.radiusDataAccessPermission}
        />
      ),
    },
    {
      name: 'Name',
      jsonKey: 'name',
    },
    {
      name: 'Created',
      formatter: (row) => dateString(row.created_at),
    },
  ];

  if (tenantState.editEnabled) {
    const onDeviceSelect = (device) => {
      const selectedDevicesCpy = { ...selectedDevices };
      if (selectedDevices[device.id]) {
        delete selectedDevicesCpy[device.id];
      } else {
        selectedDevicesCpy[device.id] = device;
      }
      dispatch({ action: 'select', payload: selectedDevicesCpy });
    };

    columns.push({
      name: 'Select',
      onClick: (devices) => {
        const selectAll = {};
        if (!Object.values(selectedDevices).some((d) => !!d)) {
          devices.forEach((device) => { selectAll[device.id] = device; });
        }
        dispatch({ action: 'select', payload: selectAll });
      },
      formatter: (device) => (
        <input
          type="checkbox"
          value={device.id}
          checked={!!selectedDevices[device.id]}
          onChange={() => onDeviceSelect(device)}
        />
      ),
    });
  }

  const searchRequest = useCallback(
    (searchStr, accept) => getTenantMacs(searchStr, accept, tenantId),
    [tenantId],
  );

  return (
    <CardSection>
      <CardControls
        disabled={isLoading}
        editEnabled={tenantState.editEnabled}
      />
      <BoxWrapper>
        <Grid container size={12}>
          <GridWrapper title="Networking Info" size={12}>
            <Grid
              container
              size={12}
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Grid size={{ lg: 2, xs: 12 }}>
                <DataFormatter
                  descriptions={[{
                    label: 'Main Router',
                    formatter: (np) => np.nas?.nas_name || '',
                  },
                  {
                    label: 'VTR',
                    key: 'ip',
                  }]}
                  data={state.nasProfile}
                />
              </Grid>
              <Grid size={{ lg: 9, xs: 12 }}>
                {(smartzoneId) && (
                <TenantWifiInfo
                  forceUpdate={forceUpdate}
                  groupDpsk={groupDpsk}
                  groupDpskIsMissing={groupDpskIsMissing}
                  smartzone={smartzone}
                />
                )}
              </Grid>
            </Grid>
          </GridWrapper>
          <Grid size={12}>
            <Divider />
          </Grid>
        </Grid>
        {tenantState.editEnabled && (
        <Section>
          <GridWrapper size={12} title="New Device">
            <DeviceForm
              afterCreate={forceUpdate}
              dot1x={dot1x}
              tenantState={tenantState}
            />
          </GridWrapper>
        </Section>
        )}
        <Section>
          <GridWrapper title="Registered Devices" size={12}>
            <Grid container>
              <Grid size={12}>
                <SimpleDataTable
                  columns={columns}
                  searchRequest={searchRequest}
                  ignoredKeys={['id']}
                  noRowsText="No devices are registered"
                  rowId="id"
                  updateCount={updateCount}
                />
              </Grid>
            </Grid>
            {tenantState.editEnabled && (
            <Grid
              container
              size={{ xs: 12 }}
              sx={{
                justifyContent: 'end',
                mt: 2,
              }}
            >
              <Grid>
                <DeviceDeleteButton
                  afterDelete={afterDelete}
                  devices={Object.values(selectedDevices)}
                />
              </Grid>
            </Grid>
            )}
          </GridWrapper>
        </Section>
      </BoxWrapper>
    </CardSection>
  );
}
